import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, TrendingDown, DollarSign, ShoppingCart, Package, 
  Users, CreditCard, BarChart3, ArrowUpRight, ArrowDownRight,
  AlertTriangle, ExternalLink, Wifi, WifiOff, Eye, MousePointerClick,
  Mail, Share2, Target, Megaphone
} from 'lucide-react';
import { Button } from '../../components/ui/button';

// ============================================================================
// MOCK DATA — will be replaced by live API feeds
// ============================================================================

const MOCK_KPIS = [
  { label: 'Revenue (MTD)', value: '$12,480', change: '+14.2%', up: true, icon: DollarSign },
  { label: 'Orders Today', value: '23', change: '+8', up: true, icon: ShoppingCart },
  { label: 'Conversion Rate', value: '3.8%', change: '+0.4%', up: true, icon: Target },
  { label: 'Avg Order Value', value: '$68.50', change: '-$2.10', up: false, icon: BarChart3 },
  { label: 'Active Customers', value: '847', change: '+32', up: true, icon: Users },
  { label: 'Ad Spend ROI', value: '4.2x', change: '+0.6x', up: true, icon: Megaphone },
];

const MOCK_SALES_WEEKLY = [
  { day: 'Mon', amount: 1840 },
  { day: 'Tue', amount: 2150 },
  { day: 'Wed', amount: 1920 },
  { day: 'Thu', amount: 2680 },
  { day: 'Fri', amount: 3100 },
  { day: 'Sat', amount: 2450 },
  { day: 'Sun', amount: 1780 },
];

const MOCK_PIPELINE = [
  { stage: 'New Leads', count: 42, value: '$28,400', color: '#F6ADBC' },
  { stage: 'Contacted', count: 28, value: '$19,200', color: '#F5C400' },
  { stage: 'Proposal Sent', count: 12, value: '$15,600', color: '#E0B000' },
  { stage: 'Negotiation', count: 6, value: '$9,800', color: '#C89E00' },
  { stage: 'Closed Won', count: 8, value: '$12,480', color: '#34C759' },
];

const MOCK_INVENTORY = [
  { name: 'Premium Starter Kit', sku: 'PSK-001', stock: 145, status: 'good' },
  { name: 'Business Essentials Pack', sku: 'BEP-002', stock: 8, status: 'low' },
  { name: 'Pro Growth Bundle', sku: 'PGB-003', stock: 67, status: 'good' },
  { name: 'Launch Package', sku: 'LP-004', stock: 3, status: 'critical' },
  { name: 'Workshop Access Pass', sku: 'WAP-005', stock: 232, status: 'good' },
];

const MOCK_TRANSACTIONS = [
  { desc: 'Shopify Payout', amount: '+$3,240.00', type: 'in', date: 'Today' },
  { desc: 'Xero — GST Payment', amount: '-$1,860.00', type: 'out', date: 'Yesterday' },
  { desc: 'Amazon Settlement', amount: '+$1,420.00', type: 'in', date: 'Jun 2' },
  { desc: 'Meta Ads', amount: '-$380.00', type: 'out', date: 'Jun 1' },
  { desc: 'Stripe Deposit', amount: '+$2,150.00', type: 'in', date: 'May 31' },
];

const MOCK_MARKETING = {
  website: { visitors: '2,340', pageviews: '8,120', bounce: '42%', source: 'Google Analytics' },
  social: [
    { platform: 'Instagram', followers: '1,240', engagement: '4.8%', change: '+120' },
    { platform: 'Facebook', followers: '890', engagement: '2.1%', change: '+45' },
    { platform: 'LinkedIn', followers: '560', engagement: '6.2%', change: '+78' },
  ],
  email: { subscribers: '1,680', openRate: '38%', clickRate: '4.2%', source: 'Mailchimp' },
  ads: { spend: '$1,240', impressions: '48,200', clicks: '1,860', cpc: '$0.67', source: 'Meta Ads' },
};

const INTEGRATIONS = [
  { name: 'Shopify', status: 'demo', color: '#96BF48' },
  { name: 'Xero', status: 'demo', color: '#13B5EA' },
  { name: 'Amazon', status: 'demo', color: '#FF9900' },
  { name: 'ASB Bank', status: 'demo', color: '#F6ADBC' },
  { name: 'Google Analytics', status: 'demo', color: '#E37400' },
  { name: 'Mailchimp', status: 'demo', color: '#FFE01B' },
  { name: 'Meta Ads', status: 'demo', color: '#1877F2' },
  { name: 'Stripe', status: 'demo', color: '#635BFF' },
];

// ============================================================================
// MINI CHART COMPONENT
// ============================================================================

const MiniBarChart = ({ data, maxHeight = 64 }) => {
  const max = Math.max(...data.map(d => d.amount));
  return (
    <div className="flex items-end gap-1.5 h-16">
      {data.map((d, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-1">
          <div
            className="w-full bg-[#F6ADBC] rounded-t-sm transition-all hover:bg-[#1E491A] group relative"
            style={{ height: `${(d.amount / max) * maxHeight}px`, minHeight: '4px' }}
          >
            <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-[#1E491A] text-white text-[10px] px-1.5 py-0.5 rounded-none opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
              ${d.amount.toLocaleString()}
            </div>
          </div>
          <span className="text-[10px] text-black/40">{d.day}</span>
        </div>
      ))}
    </div>
  );
};

const FunnelBar = ({ stage, count, value, color, maxCount }) => (
  <div className="flex items-center gap-3">
    <div className="w-24 text-xs text-black/60 text-right shrink-0">{stage}</div>
    <div className="flex-1 h-7 bg-[#F5F5F5] rounded-none overflow-hidden relative">
      <div
        className="h-full rounded-none transition-all duration-500 flex items-center px-3"
        style={{ width: `${(count / maxCount) * 100}%`, backgroundColor: color, minWidth: '60px' }}
      >
        <span className="text-xs font-bold text-[#1E491A] whitespace-nowrap">{count}</span>
      </div>
    </div>
    <div className="w-20 text-xs font-medium text-black/70 text-right shrink-0">{value}</div>
  </div>
);

// ============================================================================
// MAIN DASHBOARD
// ============================================================================

const BusinessDashboard = () => {
  const [period] = useState('This Month');
  const [liveStats, setLiveStats] = useState(null);
  const [liveLoading, setLiveLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('spring_token');
    if (!token) { setLiveLoading(false); return; }
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/platform/live-stats`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setLiveStats(d); })
      .catch(() => {})
      .finally(() => setLiveLoading(false));
  }, []);

  return (
    <div className="space-y-6 max-w-7xl mx-auto" data-testid="business-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl headline text-black mb-1" data-testid="biz-dashboard-heading">Business Dashboard</h1>
          <p className="text-black/50 text-sm">Live overview of your business performance</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[rgba(255,192,0,0.15)] text-[#1E491A] rounded-none text-xs font-medium">
            <div className="w-1.5 h-1.5 bg-[rgba(255,192,0,0.1)]0 rounded-none animate-pulse" />
            Demo Mode
          </div>
          <select className="bg-[#F0F0F0] border-0 rounded-none px-4 py-2 text-sm font-medium text-black" data-testid="period-select">
            <option>This Month</option>
            <option>This Week</option>
            <option>Today</option>
            <option>Last 90 Days</option>
          </select>
        </div>
      </div>

      {/* Live Platform Stats */}
      {liveStats && (
        <div className="bg-[#1E491A] rounded-2xl p-6 border border-[rgba(255,255,255,0.08)]" data-testid="live-platform-stats">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-[#34C759] rounded-full animate-pulse" />
              <h3 className="font-bold text-white text-sm">Platform Health — Live</h3>
            </div>
            <span className="text-[11px] text-white/30">Real-time data from SPRING</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
            <LiveStat label="Assessments" value={liveStats.assessments.total} sub={`${liveStats.assessments.today} today`} />
            <LiveStat label="Completed" value={liveStats.assessments.completed} sub={`${liveStats.assessments.completion_rate}% rate`} />
            <LiveStat label="Feedback" value={liveStats.feedback.total} sub={`${liveStats.feedback.engagement_rate}% engaged`} />
            <LiveStat label="NPS Score" value={liveStats.nps.score ?? '—'} sub={`Avg ${liveStats.nps.average ?? '—'}/10`} accent />
            <LiveStat label="Founders (A)" value={liveStats.pathways.pathway_a} />
            <LiveStat label="Employment (B)" value={liveStats.pathways.pathway_b} />
            <LiveStat label="Independent (C)" value={liveStats.pathways.pathway_c} />
          </div>
          {liveStats.weekly_activity && (
            <div className="mt-4 pt-4 border-t border-white/5">
              <p className="text-[11px] text-white/30 mb-2">Assessment Activity (Last 7 Days)</p>
              <div className="flex items-end gap-1.5 h-10">
                {liveStats.weekly_activity.map((d, i) => {
                  const max = Math.max(...liveStats.weekly_activity.map(x => x.count), 1);
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                      <div className="w-full rounded-t-sm" style={{ height: `${Math.max((d.count / max) * 32, 2)}px`, background: '#F5A800' }} />
                      <span className="text-[9px] text-white/30">{d.day}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <div className="mt-3 flex gap-4 text-[11px] text-white/25">
            <span>Pilot codes: {liveStats.pilot_codes.used}/{liveStats.pilot_codes.total} used</span>
            <span>Satisfaction: {liveStats.satisfaction.Yes} yes, {liveStats.satisfaction.Mostly} mostly, {liveStats.satisfaction.No} no</span>
          </div>
        </div>
      )}

      {/* KPI Row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="kpi-row">
        {MOCK_KPIS.map((kpi, i) => {
          const Icon = kpi.icon;
          return (
            <div key={i} className="bg-white rounded-none p-4 border border-[#E5E5E5] hover: transition-shadow" data-testid={`kpi-${i}`}>
              <div className="flex items-center justify-between mb-2">
                <Icon className="h-4 w-4 text-black/30" />
                <span className={`text-xs font-medium flex items-center gap-0.5 ${kpi.up ? 'text-[#1E491A]' : 'text-red-500'}`}>
                  {kpi.up ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                  {kpi.change}
                </span>
              </div>
              <p className="text-xl font-bold text-[#1E491A]">{kpi.value}</p>
              <p className="text-[11px] text-black/40 mt-0.5">{kpi.label}</p>
            </div>
          );
        })}
      </div>

      {/* Main Grid */}
      <div className="grid lg:grid-cols-3 gap-4">

        {/* Sales Chart — 2 cols */}
        <div className="lg:col-span-2 bg-white rounded-none border border-[#E5E5E5] p-5" data-testid="sales-chart">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-bold text-black">Weekly Sales</h3>
              <p className="text-xs text-black/40">Revenue by day</p>
            </div>
            <div className="flex items-center gap-1 text-xs text-black/40">
              <ShoppingCart className="h-3 w-3" />
              <span>Shopify + Amazon</span>
            </div>
          </div>
          <MiniBarChart data={MOCK_SALES_WEEKLY} maxHeight={80} />
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#F0F0F0]">
            <span className="text-sm text-black/50">Total this week</span>
            <span className="text-lg font-bold text-[#1E491A]">
              ${MOCK_SALES_WEEKLY.reduce((a, b) => a + b.amount, 0).toLocaleString()}
            </span>
          </div>
        </div>

        {/* Bank Balance */}
        <div className="bg-[#1E491A] rounded-none p-5 text-white" data-testid="bank-balance">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-bold">Bank Balance</h3>
              <p className="text-xs text-white/40">ASB Business Account</p>
            </div>
            <CreditCard className="h-5 w-5 text-[#F6ADBC]" />
          </div>
          <p className="text-3xl font-bold text-[#F6ADBC] mb-1">$24,680</p>
          <p className="text-xs text-white/40 mb-4">Updated 2 hours ago</p>
          <div className="space-y-2">
            {MOCK_TRANSACTIONS.slice(0, 4).map((tx, i) => (
              <div key={i} className="flex items-center justify-between py-1.5">
                <div>
                  <p className="text-sm text-white/80">{tx.desc}</p>
                  <p className="text-[10px] text-white/30">{tx.date}</p>
                </div>
                <span className={`text-sm font-medium ${tx.type === 'in' ? 'text-[#F6ADBC]' : 'text-red-400'}`}>
                  {tx.amount}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Second Row */}
      <div className="grid lg:grid-cols-2 gap-4">

        {/* Sales Pipeline */}
        <div className="bg-white rounded-none border border-[#E5E5E5] p-5" data-testid="sales-pipeline">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-bold text-black">Sales Pipeline</h3>
              <p className="text-xs text-black/40">Lead to conversion funnel</p>
            </div>
            <div className="text-right">
              <p className="text-lg font-bold text-[#1E491A]">$85,480</p>
              <p className="text-[10px] text-black/40">Pipeline value</p>
            </div>
          </div>
          <div className="space-y-2.5">
            {MOCK_PIPELINE.map((p, i) => (
              <FunnelBar key={i} {...p} maxCount={MOCK_PIPELINE[0].count} />
            ))}
          </div>
        </div>

        {/* Inventory */}
        <div className="bg-white rounded-none border border-[#E5E5E5] p-5" data-testid="inventory-widget">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-bold text-black">Inventory</h3>
              <p className="text-xs text-black/40">Stock levels across channels</p>
            </div>
            <div className="flex items-center gap-1 text-xs text-black/40">
              <Package className="h-3 w-3" />
              <span>Shopify</span>
            </div>
          </div>
          <div className="space-y-2">
            {MOCK_INVENTORY.map((item, i) => (
              <div key={i} className="flex items-center gap-3 py-2 border-b border-[#F5F5F5] last:border-0">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-black truncate">{item.name}</p>
                  <p className="text-[10px] text-black/30">{item.sku}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-bold ${
                    item.status === 'critical' ? 'text-red-500' :
                    item.status === 'low' ? 'text-orange-500' : 'text-black'
                  }`}>{item.stock}</span>
                  {item.status !== 'good' && (
                    <AlertTriangle className={`h-3.5 w-3.5 ${item.status === 'critical' ? 'text-red-500' : 'text-orange-500'}`} />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Marketing Metrics Row */}
      <div className="grid lg:grid-cols-3 gap-4" data-testid="marketing-section">

        {/* Website Traffic */}
        <div className="bg-white rounded-none border border-[#E5E5E5] p-5" data-testid="website-traffic">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-bold text-black">Website Traffic</h3>
              <p className="text-xs text-black/40">{MOCK_MARKETING.website.source}</p>
            </div>
            <Eye className="h-4 w-4 text-black/30" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <p className="text-xl font-bold text-[#1E491A]">{MOCK_MARKETING.website.visitors}</p>
              <p className="text-[10px] text-black/40">Visitors</p>
            </div>
            <div>
              <p className="text-xl font-bold text-[#1E491A]">{MOCK_MARKETING.website.pageviews}</p>
              <p className="text-[10px] text-black/40">Pageviews</p>
            </div>
            <div>
              <p className="text-xl font-bold text-[#1E491A]">{MOCK_MARKETING.website.bounce}</p>
              <p className="text-[10px] text-black/40">Bounce Rate</p>
            </div>
          </div>
          <div className="mt-4 pt-3 border-t border-[#F0F0F0]">
            <div className="flex items-center gap-2 text-xs text-black/40">
              <MousePointerClick className="h-3 w-3" />
              <span>Top source: Google Search (54%)</span>
            </div>
          </div>
        </div>

        {/* Social Media */}
        <div className="bg-white rounded-none border border-[#E5E5E5] p-5" data-testid="social-media">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-bold text-black">Social Media</h3>
              <p className="text-xs text-black/40">Cross-platform performance</p>
            </div>
            <Share2 className="h-4 w-4 text-black/30" />
          </div>
          <div className="space-y-3">
            {MOCK_MARKETING.social.map((s, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-none bg-[#F5F5F5] flex items-center justify-center text-xs font-bold text-black/50">
                  {s.platform[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-black">{s.platform}</p>
                  <p className="text-[10px] text-black/40">{s.followers} followers</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-[#1E491A]">{s.engagement}</p>
                  <p className="text-[10px] text-[#1E491A]">{s.change} this month</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Email & Ads */}
        <div className="space-y-4">
          {/* Email */}
          <div className="bg-white rounded-none border border-[#E5E5E5] p-5" data-testid="email-marketing">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-bold text-black text-sm">Email Marketing</h3>
                <p className="text-[10px] text-black/40">{MOCK_MARKETING.email.source}</p>
              </div>
              <Mail className="h-4 w-4 text-black/30" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <p className="text-lg font-bold text-[#1E491A]">{MOCK_MARKETING.email.subscribers}</p>
                <p className="text-[10px] text-black/40">Subscribers</p>
              </div>
              <div>
                <p className="text-lg font-bold text-[#1E491A]">{MOCK_MARKETING.email.openRate}</p>
                <p className="text-[10px] text-black/40">Open Rate</p>
              </div>
              <div>
                <p className="text-lg font-bold text-[#1E491A]">{MOCK_MARKETING.email.clickRate}</p>
                <p className="text-[10px] text-black/40">Click Rate</p>
              </div>
            </div>
          </div>

          {/* Paid Ads */}
          <div className="bg-white rounded-none border border-[#E5E5E5] p-5" data-testid="paid-ads">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-bold text-black text-sm">Paid Advertising</h3>
                <p className="text-[10px] text-black/40">{MOCK_MARKETING.ads.source}</p>
              </div>
              <Megaphone className="h-4 w-4 text-black/30" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <p className="text-lg font-bold text-[#1E491A]">{MOCK_MARKETING.ads.spend}</p>
                <p className="text-[10px] text-black/40">Ad Spend</p>
              </div>
              <div>
                <p className="text-lg font-bold text-[#1E491A]">{MOCK_MARKETING.ads.clicks}</p>
                <p className="text-[10px] text-black/40">Clicks</p>
              </div>
            </div>
            <div className="mt-2 flex items-center gap-2 text-xs text-black/40">
              <Target className="h-3 w-3" />
              <span>CPC: {MOCK_MARKETING.ads.cpc} | Impressions: {MOCK_MARKETING.ads.impressions}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Connected Integrations */}
      <div className="bg-white rounded-none border border-[#E5E5E5] p-5" data-testid="integrations-bar">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-bold text-black">Data Sources</h3>
            <p className="text-xs text-black/40">Connect your tools to see live data</p>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
          {INTEGRATIONS.map((int, i) => (
            <button
              key={i}
              className="flex flex-col items-center gap-2 p-3 rounded-none border border-[#E5E5E5] hover:border-[#F6ADBC] hover:shadow-sm transition-all group"
              data-testid={`integration-${int.name.toLowerCase().replace(/\s/g, '-')}`}
            >
              <div
                className="w-10 h-10 rounded-none flex items-center justify-center text-white text-xs font-bold"
                style={{ backgroundColor: int.color }}
              >
                {int.name.slice(0, 2).toUpperCase()}
              </div>
              <span className="text-[10px] font-medium text-black/60 group-hover:text-black">{int.name}</span>
              <span className="text-[9px] px-2 py-0.5 rounded-none bg-[#F5F5F5] text-black/40">Connect</span>
            </button>
          ))}
        </div>
      </div>

      {/* Demo Notice */}
      <div className="bg-[#FFFEF5] border border-[#F6ADBC]/30 rounded-none p-4 flex items-start gap-3" data-testid="demo-notice">
        <div className="w-8 h-8 rounded-none bg-[#F6ADBC]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
          <WifiOff className="h-4 w-4 text-[#C89E00]" />
        </div>
        <div>
          <p className="text-sm font-medium text-[#1E491A]">Demo Mode — Sample Data</p>
          <p className="text-xs text-black/50 mt-1">
            This dashboard shows what your live business data will look like once you connect your tools. 
            Connect Shopify, Xero, your bank, Google Analytics, and more to see real-time metrics.
          </p>
        </div>
      </div>
    </div>
  );
};

const LiveStat = ({ label, value, sub, accent }) => (
  <div className="text-center">
    <div className={`text-xl font-bold ${accent ? 'text-[#F5A800]' : 'text-white'}`}>{value}</div>
    <div className="text-[11px] text-white/50">{label}</div>
    {sub && <div className="text-[10px] text-white/25 mt-0.5">{sub}</div>}
  </div>
);


export default BusinessDashboard;
